import React from "react";
import {
    Mail,
    Phone,
    MapPin,
    BriefcaseIcon,
    GraduationCap,
    Award,
    User,
    Code,
    Trophy,
    CheckSquare,
} from "lucide-react";
import { cn } from "@util/tailwind";
import SkillBadge from "@component/skill-badge";
import ExperienceTag, {
    TagType,
    filterTagsByContext,
} from "@component/experience-tag";
import { CvData } from "@type/candidate";

interface ProfileTabContentProps {
    candidate: CvData;
    activeSection: string;
    scrollToSection: (section: string) => void;
    sectionRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

const ProfileTabContent: React.FC<ProfileTabContentProps> = ({
    candidate,
    activeSection,
    scrollToSection,
    sectionRefs,
}) => {
    // CV-less flow candidates (auto-start / CV-optional entry, #3077/#3090) reach
    // the drawer without a parsed CV — the whole tab reads candidate.CV_data.*,
    // so guard once and show a placeholder instead of crashing.
    if (!candidate?.CV_data) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                <User className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No CV on file yet</p>
                <p className="text-xs text-gray-500 max-w-xs">
                    This candidate hasn&apos;t submitted a resume yet. Their profile will appear here once a CV is collected.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Section navigation tabs */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-1 flex flex-wrap sticky top-0 z-10">
                <button
                    onClick={() => scrollToSection("personal")}
                    className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                        activeSection === "personal"
                            ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    )}
                >
                    <div className="flex items-center">
                        <User className="h-3 w-3 mr-1" />

                        <span>Personal Information</span>
                    </div>
                </button>
                <button
                    onClick={() => scrollToSection("skills")}
                    className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                        activeSection === "skills"
                            ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    )}
                >
                    <div className="flex items-center">
                        <Award className="h-3 w-3 mr-1" />

                        <span>Skills</span>
                    </div>
                </button>
                <button
                    onClick={() => scrollToSection("experience")}
                    className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                        activeSection === "experience"
                            ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    )}
                >
                    <div className="flex items-center">
                        <BriefcaseIcon className="h-3 w-3 mr-1" />

                        <span>Experience</span>
                    </div>
                </button>
                <button
                    onClick={() => scrollToSection("education")}
                    className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                        activeSection === "education"
                            ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    )}
                >
                    <div className="flex items-center">
                        <GraduationCap className="h-3 w-3 mr-1" />

                        <span>Education</span>
                    </div>
                </button>
                {(candidate.CV_data.projects || []).length > 0 && (
                    <button
                        onClick={() => scrollToSection("projects")}
                        className={cn(
                            "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                            activeSection === "projects"
                                ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                        )}
                    >
                        <div className="flex items-center">
                            <Code className="h-3 w-3 mr-1" />

                            <span>Projects</span>
                        </div>
                    </button>
                )}
                {(candidate.CV_data.awards || []).length > 0 && (
                    <button
                        onClick={() => scrollToSection("awards")}
                        className={cn(
                            "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                            activeSection === "awards"
                                ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                        )}
                    >
                        <div className="flex items-center">
                            <Trophy className="h-3 w-3 mr-1" />

                            <span>Awards</span>
                        </div>
                    </button>
                )}
                {candidate.CV_data.certifications.length > 0 && (
                    <button
                        onClick={() => scrollToSection("certifications")}
                        className={cn(
                            "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                            activeSection === "certifications"
                                ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                        )}
                    >
                        <div className="flex items-center">
                            <CheckSquare className="h-3 w-3 mr-1" />

                            <span>Certifications</span>
                        </div>
                    </button>
                )}
            </div>

            {/* Personal Information Section (Combined Personal and Summary) */}
            <div
                ref={(el) => { sectionRefs.current.personal = el; }}
                id="personal-section"
                className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
            >
                <h4 className="font-medium text-base mb-2">
                    Personal Information
                </h4>

                <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm">
                        <Mail className="h-5 w-5 mr-3 text-gray-600 dark:text-gray-400" />
                        <span>{candidate.CV_data.email || "No Email"}</span>
                    </div>
                    <div className="flex items-center text-sm">
                        <Phone className="h-5 w-5 mr-3 text-gray-600 dark:text-gray-400" />
                        <span>{(candidate.CV_data.country_code || "") == "" ? "" : candidate.CV_data.country_code}{candidate.CV_data.phone || "No Phone Number"}</span>
                    </div>
                    <div className="flex items-center text-sm">
                        <MapPin className="h-5 w-5 mr-3 text-gray-600 dark:text-gray-400" />
                        <span>
                            {candidate.CV_data.location || "No Location Provided"}
                        </span>
                    </div>
                </div>

                <h4 className="font-medium text-base mb-2">
                    Professional Summary
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    {candidate.CV_data.professional_summary || "No professional summary provided."}
                </p>
            </div>

            {/* Skills Section */}
            {(candidate.CV_data.skills || []).length > 0 && (
                <div
                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                    ref={(el) => { sectionRefs.current.skills = el; }}
                    id="skills-section"
                >
                    <div className="flex items-center gap-3 mb-4 pb-2">
                        <Code className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h4 className="font-medium text-base">Skills</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {(candidate.CV_data.skills || []).slice(0, 20).map((skill, index) => (
                            <SkillBadge key={index} label={skill} />
                        ))}
                    </div>
                </div>
            )}

            {/* Work Experience Section */}
            {(candidate.CV_data.work_experience || []).length > 0 && (
                <div
                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                    ref={(el) => { sectionRefs.current.experience = el; }}
                    id="experience-section"
                >
                    <div className="flex items-center gap-3 mb-6 pb-2">
                        <BriefcaseIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h4 className="font-medium text-base">
                            Work Experience
                        </h4>
                    </div>

                    <div className="space-y-0">
                        <div className="relative">
                            {/* Timeline items */}
                            {(candidate.CV_data.work_experience || []).map((experience, index) => (
                                <div
                                    key={index}
                                    className={cn("relative pl-12", {
                                        "pb-8": index < candidate.CV_data.work_experience.length - 1,
                                    })}
                                >
                                    {/* Timeline line - only show for non-last items */}
                                    {index < candidate.CV_data.work_experience.length - 1 && (
                                        <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30"></div>
                                    )}

                                    {/* Icon */}
                                    <div className="absolute left-0 top-1">
                                        <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400">
                                            <BriefcaseIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div>
                                        <div className="mb-1 relative">
                                            <div className="flex flex-col gap-2">
                                                <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left w-full pr-24">
                                                    {experience.role}
                                                    {" "}
                                                    {(experience.tags && experience.tags.length > 0) && (
                                                        <>
                                                            {filterTagsByContext(
                                                                experience.tags as TagType[],
                                                                "work"
                                                            ).map((tag, tagIndex) => (
                                                                <ExperienceTag
                                                                    key={tagIndex}
                                                                    type={tag}
                                                                />
                                                            ))}
                                                        </>
                                                    )}
                                                </h5>
                                            </div>

                                            <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                                {experience.period_end_year === 9999 ? (
                                                    <>
                                                        {experience.period_start_year} - Present
                                                    </>
                                                ) : (
                                                    <>
                                                        {experience.period_start_year} - {experience.period_end_year}
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                        {(experience.institution || experience.location) && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                                {experience.institution}{experience.location && ` • ${experience.location}`}
                                            </p>
                                        )}
                                        {experience.description && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {experience.description}
                                            </p>
                                        )}

                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Education Section */}
            {(candidate.CV_data.education || []).length > 0 && (
                <div
                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                    ref={(el) => { sectionRefs.current.education = el; }}
                    id="education-section"
                >
                    <div className="flex items-center gap-3 mb-6 pb-2">
                        <GraduationCap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h4 className="font-medium text-base">Education</h4>
                    </div>
                    <div className="space-y-0">
                        <div className="relative">
                            {/* Education items */}
                            {candidate.CV_data.education.map((edu, index) => (
                                <div key={index} className={cn("relative pl-12", {
                                    "pb-8": index < candidate.CV_data.education.length - 1,
                                })}>
                                    {/* Timeline line - only show for non-last items */}
                                    {index < candidate.CV_data.education.length - 1 && (
                                        <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30" style={{
                                            backgroundColor: "#dcfce7",
                                        }}></div>
                                    )}

                                    {/* Icon */}
                                    <div className="absolute left-0 top-1">
                                        <div
                                            className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400"
                                            style={{
                                                backgroundColor: "#dcfce7",
                                                border: "2px solid #22c55e",
                                            }}
                                        >
                                            <GraduationCap
                                                className="h-3 w-3 text-blue-600 dark:text-blue-400"
                                                style={{
                                                    color: "#22c55e",
                                                    border: "0px solid #22c55e",
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div>
                                        <div className="mb-1 relative">
                                            <div className="flex flex-col gap-2">
                                                <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left pr-24">
                                                    {edu.degree}
                                                    {" "}
                                                    {edu.tags && edu.tags.length > 0 && (
                                                        <>
                                                            {filterTagsByContext(
                                                                edu.tags as TagType[],
                                                                "education"
                                                            ).map((tag, tagIndex) => (
                                                                <ExperienceTag
                                                                    key={tagIndex}
                                                                    type={tag}
                                                                />
                                                            ))}
                                                        </>
                                                    )}
                                                </h5>
                                            </div>
                                            {edu.period && (
                                                <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                                    {edu.period}
                                                </span>
                                            )}
                                        </div>
                                        {(edu.institution || edu.location) && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                                {edu.institution}{`${edu.location && ` • ${edu.location}`}`}
                                            </p>
                                        )}
                                        {edu.description && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {edu.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Projects Section */}
            {(candidate.CV_data.projects || []).length > 0 && (
                <div
                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                    ref={(el) => { sectionRefs.current.projects = el; }}
                    id="projects-section"
                >
                    <div className="flex items-center gap-3 mb-6 pb-2">
                        <Code className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h4 className="font-medium text-base">Projects</h4>
                    </div>
                    <div className="space-y-0">
                        <div className="relative">
                            {/* Project items */}
                            {candidate.CV_data.projects.map((project, index) => (
                                <div key={index} className={cn("relative pl-12", {
                                    "pb-8": index < candidate.CV_data.projects.length - 1,
                                })}>
                                    {/* Timeline line - only show for non-last items */}
                                    {index < candidate.CV_data.projects.length - 1 && (
                                        <div
                                            className="absolute left-3 top-1 bottom-0 w-[2px]"
                                            style={{ backgroundColor: "#f3e8ff" }}
                                        ></div>
                                    )}

                                    {/* Icon */}
                                    <div className="absolute left-0 top-1">
                                        <div
                                            className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2"
                                            style={{
                                                backgroundColor: "#f3e8ff",
                                                border: "1px solid #7c3aed",
                                            }}
                                        >
                                            <Code
                                                className="h-3 w-3"
                                                style={{ color: "#9333ea" }}
                                            />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div>
                                        <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                                            <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left ">
                                                {project.name}
                                                {" "}
                                                {project.role && (
                                                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                                                        {project.role}
                                                    </span>
                                                )}
                                            </h5>
                                            {project.year && (
                                                <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                                    {project.year}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                            {project.description}
                                        </p>

                                        {/* Technologies */}
                                        {project.tech_stack.length > 0 && (
                                            <div className="mb-3 flex flex-row flex-wrap items-center gap-2">
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    Technologies:
                                                </p>
                                                {project.tech_stack.map(
                                                    (tech, techIndex) => (
                                                        <span key={techIndex} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded-md">
                                                            {tech}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        )}
                                        {/* Links section removed as requested */}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}


            {/* Awards Section */}
            {(candidate.CV_data.awards || []).length > 0 && (
                <div
                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                    ref={(el) => { sectionRefs.current.awards = el; }}
                    id="awards-section"
                >
                    <div className="flex items-center gap-3 mb-6 pb-2">
                        <Trophy className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h4 className="font-medium text-base">Awards</h4>
                    </div>
                    <div className="space-y-0">
                        <div className="relative">
                            {/* Awards items */}
                            {candidate.CV_data.awards.map((award, index) => (
                                <div key={index} className={cn("relative pl-12", {
                                    "pb-8": index < candidate.CV_data.awards.length - 1,
                                })}>
                                    {/* Timeline line - only show for non-last items */}
                                    {index < candidate.CV_data.awards.length - 1 && (
                                        <div
                                            className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30"
                                            style={{ backgroundColor: "#fef3c7" }}
                                        ></div>
                                    )}

                                    {/* Icon */}
                                    <div className="absolute left-0 top-1">
                                        <div
                                            className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400"
                                            style={{
                                                backgroundColor: "#fef3c7",
                                                border: "1.81818px solid #f59e0b",
                                            }}
                                        >
                                            <Trophy
                                                className="h-3 w-3 text-blue-600 dark:text-blue-400"
                                                style={{ color: "#d97706" }}
                                            />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div>
                                        <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                                            <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left w-full">
                                                {award.name}
                                            </h5>
                                            {award.date && (
                                                <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                                    {award.date}
                                                </span>
                                            )}
                                        </div>
                                        {award.institution && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                                {award.institution}
                                            </p>
                                        )}
                                        {award.description && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {award.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {/* Certifications Section */}
            {candidate.CV_data.certifications.length > 0 && (
                <div
                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                    ref={el => { sectionRefs.current.certifications = el; }}
                    id="certifications-section"
                >
                    <div className="flex items-center gap-3 mb-6 pb-2">
                        <CheckSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <h4 className="font-medium text-base">Certifications</h4>
                    </div>
                    <div className="space-y-0">
                        <div className="relative">
                            {/* Certification items */}
                            {(candidate.CV_data.certifications || []).map(
                                (certification, index) => (
                                    <div
                                        key={index}
                                        className={cn("relative pl-12", {
                                            "pb-8": index < candidate.CV_data.certifications.length - 1,
                                        })}
                                    >
                                        {/* Timeline line - only show for non-last items */}
                                        {index < (candidate.CV_data.certifications || []).length - 1 && (
                                            <div
                                                className="absolute left-3 top-1 bottom-0 w-[2px]"
                                                style={{ backgroundColor: "#fce7f3" }}
                                            ></div>
                                        )}

                                        {/* Icon */}
                                        <div className="absolute left-0 top-1">
                                            <div
                                                className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2"
                                                style={{
                                                    backgroundColor: "#fce7f3",
                                                    border: "0.909091px solid #db2777",
                                                }}
                                            >
                                                <CheckSquare
                                                    className="h-3 w-3"
                                                    style={{ color: "#db2777" }}
                                                />
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div>
                                            <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                                                <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left">
                                                    {certification.name}
                                                </h5>
                                                {certification.date && (
                                                    <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                                        {certification.date}
                                                        {!!certification.expiration_date &&
                                                            ` (Expires: ${certification.expiration_date})`}
                                                    </span>
                                                )}

                                            </div>
                                            {certification.institution && (
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                                    {certification.institution}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileTabContent;
